import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';
import { login, logout } from './authSlice';
import { getStorage } from 'shared/platform/adapter';

const st = () => getStorage();
const getStored = (k) => st()?.getItem(k) ?? null;
const setStored = (k, v) => { const s = st(); if (s) s.setItem(k, v); };
const removeStored = (k) => { const s = st(); if (s) s.removeItem(k); };

const STORAGE_KEY = 'messagesState.v1';

const loadPersistedState = (currentUserId) => {
  try {
    const raw = getStored(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.userId && currentUserId && parsed.userId !== currentUserId) {
      removeStored(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

const persistState = (state, userId) => {
  try {
    const payload = {
      userId: userId,
      chats: state.chats,
      chatMessages: state.chatMessages,
      unreadCounts: state.unreadCounts,
      hasMoreChats: state.hasMoreChats,
    };
    setStored(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // ignore
  }
};

// Async thunk для отправки сообщения
export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async ({ chatId, content }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.sendMessage(chatId, { content });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send message');
    }
  }
);

// Async thunk для получения сообщений чата
export const fetchChatMessages = createAsyncThunk(
  'messages/fetchChatMessages',
  async ({ chatId, limit = 50, offset = 0 }, { rejectWithValue, getState }) => {
    try {
      const response = await socialAPI.getChatMessages(chatId, { limit, offset });
      // Бэкенд возвращает {messages: [...]} 
      const messages = response.data?.messages || response.data || [];
      const currentUserId = getState().auth?.user?.id || null;
      return { chatId, messages, hasMore: messages.length === limit, currentUserId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

// Async thunk для получения списка чатов
export const fetchUserChats = createAsyncThunk(
  'messages/fetchUserChats',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      // console.log('📋 fetchUserChats: calling API with limit=', limit, 'offset=', offset); // Закомментировано для продакшена
      const response = await socialAPI.getUserChats({ limit, offset });
      // console.log('📋 fetchUserChats: API response:', response.data); // Закомментировано для продакшена
      const chats = response.data?.chats || [];
      const hasMore = response.data?.hasMore || false;
      // console.log('📋 fetchUserChats: parsed chats count=', chats.length, 'hasMore=', hasMore); // Закомментировано для продакшена
      return { chats, hasMore };
    } catch (error) {
      // Подавляем логирование временных ошибок сервера (5xx), так как они не критичны
      const status = error.response?.status;
      if (status && status >= 500 && status < 600) {
        // Временные ошибки сервера - не логируем
        return rejectWithValue('Server temporarily unavailable');
      }
      // Логируем только клиентские ошибки (4xx) и другие ошибки
      if (status && status < 500) {
        console.error('❌ fetchUserChats: API error:', error);
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch chats');
    }
  }
);

// Async thunk для создания нового чата
export const createChat = createAsyncThunk(
  'messages/createChat',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.createChat(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create chat');
    }
  }
);

// Async thunk для отметки сообщений как прочитанных
export const markMessagesAsRead = createAsyncThunk(
  'messages/markAsRead',
  async ({ chatId, messageIds }, { rejectWithValue }) => {
    try {
      await socialAPI.markMessagesAsRead(chatId, messageIds);
      return { chatId, messageIds };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark messages as read');
    }
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState: {
    // Список чатов пользователя
    chats: [],
    // Сообщения по чатам: { chatId: { messages: [], hasMore: boolean, isLoading: boolean } }
    chatMessages: {},
    // Непрочитанные сообщения по чатам: { chatId: number }
    unreadCounts: {},
    currentUserId: null,
    // Общее состояние
    isLoading: false,
    error: null,
    // Флаг для обнаружения устаревших чатов (из старого in-memory хранилища)
    needsChatRefresh: false,
    hasMoreChats: true,
  },
  reducers: {
    // Добавление нового сообщения (приходит через WebSocket)
    addMessage: (state, action) => {
      const payload = action.payload || {};
      const rawMessage = payload.message || payload;


      if (!rawMessage) {
        return;
      }

      // Определяем текущего пользователя
      let currentUserId = payload.currentUserId;
      if (currentUserId === undefined || currentUserId === null) {
        currentUserId = state.currentUserId ?? null;
      } else {
        state.currentUserId = currentUserId;
      }

      const chatId = rawMessage.chatId ?? rawMessage.chat_id;
      if (!chatId) {
        return;
      }

      const senderId = rawMessage.user?.id ?? rawMessage.userId ?? rawMessage.user_id ?? null;
      const normalizedMessage = {
        id: rawMessage.id,
        chatId,
        content: rawMessage.content ?? rawMessage.text ?? '',
        createdAt: rawMessage.createdAt ?? rawMessage.created_at ?? new Date().toISOString(),
        isRead: rawMessage.isRead ?? rawMessage.is_read ?? false,
        isDelivered: rawMessage.isDelivered ?? rawMessage.is_delivered ?? true,
        user: rawMessage.user ?? (senderId !== null ? { id: senderId } : undefined),
        ...rawMessage,
      };

      normalizedMessage.chatId = chatId;
      if (!normalizedMessage.user) {
        normalizedMessage.user = { id: senderId };
      }

      const isOwnMessage = currentUserId !== null && normalizedMessage.user?.id === currentUserId;

      // Добавляем сообщение в чат
      if (!state.chatMessages[chatId]) {
        state.chatMessages[chatId] = { messages: [], hasMore: true, isLoading: false, needsHistoryLoad: true };
      }

      // Проверяем, нет ли уже такого сообщения
      const exists = state.chatMessages[chatId].messages.find(m => m.id === normalizedMessage.id);
      if (!exists) {
        const messageToStore = { ...normalizedMessage };

        if (state.activeChatId === chatId && !isOwnMessage) {
          messageToStore.isRead = true;
        }

        state.chatMessages[chatId].messages.push(messageToStore);

        // Обновляем непрочитанные сообщения (учитываем только входящие)
        if (!isOwnMessage && !messageToStore.isRead) {
          state.unreadCounts[chatId] = (state.unreadCounts[chatId] || 0) + 1;
        }
      } else {
      }

      // Обновляем чаты (создаём новый, если его ещё нет)
      let chat = state.chats.find(c => c.id === chatId);
      if (!chat) {
        chat = {
          id: chatId,
          members: normalizedMessage.members || [],
          participants: normalizedMessage.participants || normalizedMessage.members || [],
          lastMessage: normalizedMessage,
          lastMessageAt: normalizedMessage.createdAt,
        };
        state.chats.unshift(chat);
        console.log('Redux addMessage: chat added to list, total chats:', state.chats.length);
      } else {
        chat.lastMessage = normalizedMessage;
        chat.lastMessageAt = normalizedMessage.createdAt;
        // Перемещаем чат в начало списка при новом сообщении
        state.chats = [chat, ...state.chats.filter(c => c.id !== chatId)];
      }
      persistState(state, state.currentUserId);
    },

    // Обновление сообщения
    updateMessage: (state, action) => {
      const { id, updates } = action.payload;
      
      // Ищем сообщение во всех чатах
      Object.keys(state.chatMessages).forEach(chatId => {
        const messageIndex = state.chatMessages[chatId].messages.findIndex(m => m.id === id);
        if (messageIndex !== -1) {
          state.chatMessages[chatId].messages[messageIndex] = {
            ...state.chatMessages[chatId].messages[messageIndex],
            ...updates
          };
        }
      });
    },

    // Обновление статуса нескольких сообщений (для прочтения через WebSocket)
    updateMessagesStatus: (state, action) => {
      const { chatId, messageIds, isRead } = action.payload;
      
      console.log('🔔 Redux updateMessagesStatus:', { 
        chatId, 
        messageIds, 
        isRead,
        messageCount: messageIds?.length || 0,
        hasChat: !!state.chatMessages[chatId]
      });
      
      if (!state.chatMessages[chatId]) {
        console.warn('⚠️ Redux updateMessagesStatus: chat not found', chatId);
        return;
      }

      let updatedCount = 0;
      const messageIdsSet = new Set(messageIds.map(id => Number(id))); // Преобразуем в числа для сравнения
      
      // Создаем НОВЫЙ массив сообщений для триггера ре-рендера
      state.chatMessages[chatId].messages = state.chatMessages[chatId].messages.map(message => {
        const messageIdNum = Number(message.id);
        if (messageIdsSet.has(messageIdNum)) {
          const wasBefore = { isRead: message.isRead, isDelivered: message.isDelivered };
          updatedCount++;
          console.log('🔔 Redux updateMessagesStatus: updated message', message.id, 'type:', typeof message.id, 'before:', wasBefore, 'after:', { isRead, isDelivered: true });
          
          // Возвращаем НОВЫЙ объект сообщения
          return {
            ...message,
            isRead: isRead,
            isDelivered: isRead ? true : message.isDelivered
          };
        }
        return message;
      });
      
      console.log('🔔 Redux updateMessagesStatus: updated', updatedCount, 'messages');
    },

    // Очистка всех чатов (для сброса устаревших in-memory чатов)
    clearAllChats: (state) => {
      state.chats = [];
      state.chatMessages = {};
      state.unreadCounts = {};
      state.needsChatRefresh = false;
      state.currentUserId = null;
      try {
        removeStored(STORAGE_KEY);
      } catch (_) {
        // ignore
      }
    },
    
    // Установка текущего пользователя и проверка сохраненных данных
    setCurrentUser: (state, action) => {
      const userId = action.payload;
      
      // Если пользователь изменился, очищаем все чаты
      if (state.currentUserId && state.currentUserId !== userId) {
        state.chats = [];
        state.chatMessages = {};
        state.unreadCounts = {};
        state.needsChatRefresh = false;
        try {
          removeStored(STORAGE_KEY);
        } catch (_) {
          // ignore
        }
      }
      
      state.currentUserId = userId;
      
      // Загружаем сохраненные данные для нового пользователя
      const persisted = loadPersistedState(userId);
      if (persisted) {
        state.chats = persisted.chats || [];
        state.chatMessages = persisted.chatMessages || {};
        state.unreadCounts = persisted.unreadCounts || {};
        state.hasMoreChats = persisted.hasMoreChats ?? true;
      }
    },

    // Удаление сообщения
    removeMessage: (state, action) => {
      const messageId = action.payload;
      
      Object.keys(state.chatMessages).forEach(chatId => {
        state.chatMessages[chatId].messages = state.chatMessages[chatId].messages.filter(
          m => m.id !== messageId
        );
      });
    },

    // Оптимистичное добавление сообщения
    addMessageOptimistic: (state, action) => {
      const { chatId, content, user } = action.payload;
      const tempMessage = {
        id: `temp_${Date.now()}`,
        chatId,
        content,
        user,
        createdAt: new Date().toISOString(),
        isRead: false,
        isOptimistic: true
      };
      
      console.log('⏳ addMessageOptimistic: adding temp message', {
        tempId: tempMessage.id,
        chatId,
        content
      });
      
      if (!state.chatMessages[chatId]) {
        state.chatMessages[chatId] = { messages: [], hasMore: true, isLoading: false };
      }
      
      // Создаем новый массив с добавленным сообщением
      state.chatMessages[chatId].messages = [
        ...state.chatMessages[chatId].messages,
        tempMessage
      ];
      
      // Обновляем информацию о чате
      const chat = state.chats.find(c => c.id === chatId);
      if (chat) {
        chat.lastMessage = tempMessage;
        chat.lastMessageAt = tempMessage.createdAt;
      }
      persistState(state, state.currentUserId);
    },

    // Обновление статуса прочтения
    updateReadStatus: (state, action) => {
      const { chatId, messageIds, isRead } = action.payload;
      
      if (state.chatMessages[chatId]) {
        state.chatMessages[chatId].messages.forEach(message => {
          if (messageIds.includes(message.id)) {
            message.isRead = isRead;
          }
        });
      }
      
      // Обновляем счетчик непрочитанных
      if (isRead) {
        state.unreadCounts[chatId] = 0;
      }
    },

    // Установка активного чата и моментальная пометка сообщений как прочитанных
    setActiveChat: (state, action) => {
      let chatId = null;
      let currentUserId = state.currentUserId ?? null;

      if (typeof action.payload === 'object' && action.payload !== null) {
        chatId = action.payload.chatId ?? null;
        if (action.payload.currentUserId !== undefined) {
          currentUserId = action.payload.currentUserId;
        }
      } else {
        chatId = action.payload ?? null;
      }

      console.log('Redux setActiveChat: chatId=', chatId, 'currentUserId=', currentUserId);
      state.currentUserId = currentUserId;
      state.activeChatId = chatId;
      console.log('Redux setActiveChat: state.activeChatId now =', state.activeChatId);

      if (chatId && state.chatMessages[chatId]) {
        state.unreadCounts[chatId] = 0;
      }
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс флага needsChatRefresh
    clearNeedsChatRefresh: (state) => {
      state.needsChatRefresh = false;
    },

    // Сброс состояния
    resetMessagesState: (state) => {
      state.chats = [];
      state.chatMessages = {};
      state.unreadCounts = {};
      state.isLoading = false;
      state.error = null;
      state.hasMoreChats = true;
      state.activeChatId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Отправка сообщения
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        const message = action.payload;
        const { chatId } = message;
        
        console.log('✅ sendMessage.fulfilled: HTTP response received!', {
          messageId: message.id,
          chatId,
          content: message.content,
          optimisticMessages: state.chatMessages[chatId]?.messages?.filter(m => m.isOptimistic).length || 0
        });
        
        // Проверяем, есть ли чат в списке, если нет - добавляем или обновляем список
        const existingChat = state.chats.find(c => c.id === chatId);
        if (!existingChat) {
          console.log('⚠️ Chat not in list, need to refresh chat list');
          // Устанавливаем флаг для обновления списка чатов
          state.needsChatRefresh = true;
        } else {
          // Обновляем lastMessage и lastMessageAt для чата
          existingChat.lastMessage = message;
          existingChat.lastMessageAt = message.createdAt || message.created_at || new Date().toISOString();
          // Перемещаем чат в начало списка
          state.chats = [existingChat, ...state.chats.filter(c => c.id !== chatId)];
        }
        
        // Заменяем оптимистичное сообщение на реальное
        if (state.chatMessages[chatId]) {
          const optimisticMessages = state.chatMessages[chatId].messages.filter(m => m.isOptimistic);
          console.log('🔍 Looking for optimistic message:', {
            searchContent: message.content,
            optimisticCount: optimisticMessages.length,
            optimisticMessages: optimisticMessages.map(m => ({id: m.id, content: m.content}))
          });
          
          const optimisticIndex = state.chatMessages[chatId].messages.findIndex(
            m => m.isOptimistic && m.content === message.content
          );
          if (optimisticIndex !== -1) {
            console.log('✅ Replacing optimistic message at index', optimisticIndex, 'with real message:', message.id);
            // Создаем НОВЫЙ массив с замененным сообщением
            const newMessages = [
              ...state.chatMessages[chatId].messages.slice(0, optimisticIndex),
              message,
              ...state.chatMessages[chatId].messages.slice(optimisticIndex + 1)
            ];
            console.log('✅ New messages array length:', newMessages.length, 'has optimistic?', newMessages.some(m => m.isOptimistic));
            state.chatMessages[chatId].messages = newMessages;
          } else {
            console.log('⚠️ No optimistic message found, adding new message');
            state.chatMessages[chatId].messages = [
              ...state.chatMessages[chatId].messages,
              message
            ];
          }
        }
        persistState(state, state.currentUserId);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        
        console.error('❌ sendMessage.rejected: HTTP request failed!', {
          error: action.payload,
          errorDetails: action.error
        });
        
        // Если ошибка связана с тем, что чат не найден в БД (старый in-memory ID)
        // устанавливаем флаг для очистки чатов
        if (action.payload && typeof action.payload === 'string' && action.payload.includes('Chat not found')) {
          state.needsChatRefresh = true;
        }
      })
      
      // Получение сообщений чата
      .addCase(fetchChatMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg;
        
        if (!state.chatMessages[chatId]) {
          state.chatMessages[chatId] = { messages: [], hasMore: true, isLoading: false };
        }
        state.chatMessages[chatId].isLoading = true;
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        const { chatId, messages, hasMore, currentUserId } = action.payload;
        const { offset = 0 } = action.meta?.arg || {};

        console.log('🔄 fetchChatMessages.fulfilled:', {
          chatId,
          fetchedCount: messages.length,
          existingCount: state.chatMessages[chatId]?.messages?.length || 0,
          activeChatId: state.activeChatId
        });

        if (currentUserId !== undefined && currentUserId !== null) {
          state.currentUserId = currentUserId;
        }
        
        if (!Array.isArray(messages)) {
          console.error('fetchChatMessages.fulfilled: messages is not an array', messages);
          return;
        }
        
        const existingMessages = state.chatMessages[chatId]?.messages || [];
        console.log('🔄 Existing messages before merge:', existingMessages.length);
        
        // Первая загрузка
        if (existingMessages.length === 0) {
          state.chatMessages[chatId] = { messages, hasMore, isLoading: false };

          if (state.activeChatId === chatId) {
            state.unreadCounts[chatId] = 0;
          } else {
            const unreadCount = messages.filter(m => 
              m.user?.id !== currentUserId && !m.isRead
            ).length;
            state.unreadCounts[chatId] = unreadCount;
          }

          // ВАЖНО: Обновляем lastMessage в списке чатов, если есть сообщения
          // Это критично для отображения последнего сообщения после перезагрузки страницы
          if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            let chat = state.chats.find(c => c.id === chatId);
            if (!chat) {
              // Если чата нет в списке, создаем его (может быть после перезагрузки)
              chat = {
                id: chatId,
                type: 'private',
                members: [],
                participants: [],
                lastMessage: lastMsg,
                lastMessageAt: lastMsg.createdAt || lastMsg.created_at,
              };
              state.chats.push(chat);
            } else {
              // Обновляем существующий чат
              chat.lastMessage = lastMsg;
              chat.lastMessageAt = lastMsg.createdAt || lastMsg.created_at;
            }
          }

          persistState(state, state.currentUserId);
          return;
        }
        // Если это подгрузка старой истории (offset > 0) — ПРЕПЕНДИМ
        if (offset > 0) {
          const existingIds = new Set(existingMessages.map(m => m.id));
          const olderToAdd = messages.filter(m => !existingIds.has(m.id));
          state.chatMessages[chatId] = {
            messages: [...olderToAdd, ...existingMessages],
            hasMore,
            isLoading: false,
          };
          persistState(state, state.currentUserId);
          return;
        }

        // Проверяем есть ли новые сообщения или изменения в статусах
        const lastExistingId = existingMessages[existingMessages.length - 1]?.id;
        const lastFetchedId = messages[messages.length - 1]?.id;
        
        // Создаем map существующих сообщений для быстрого поиска
        const existingMessagesMap = new Map(existingMessages.map(m => [m.id, m]));
        let hasChanges = false;
        
        // Проверяем каждое сообщение на изменения статусов
        messages.forEach(newMsg => {
          const existingMsg = existingMessagesMap.get(newMsg.id);
          
          if (!existingMsg) {
            // Новое сообщение
            hasChanges = true;
          } else {
            // Проверяем изменения статусов
            if (existingMsg.isRead !== newMsg.isRead || 
                existingMsg.isDelivered !== newMsg.isDelivered ||
                existingMsg.content !== newMsg.content) {
              hasChanges = true;
            }
          }
        });
        
        // Если последнее сообщение то же самое и нет изменений - ничего не меняем
        if (lastExistingId === lastFetchedId && existingMessages.length === messages.length && !hasChanges) {
          console.log('🔄 No changes detected, keeping existing messages');
          state.chatMessages[chatId].isLoading = false;
          
          // Все равно обновляем lastMessage в списке чатов на случай, если его там нет
          if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            const chat = state.chats.find(c => c.id === chatId);
            if (chat && !chat.lastMessage) {
              chat.lastMessage = lastMsg;
              chat.lastMessageAt = lastMsg.createdAt || lastMsg.created_at;
            }
          }
          
          persistState(state, state.currentUserId);
          return;
        }
        
        // Если в store есть более новые сообщения чем от сервера - НЕ заменяем!
        if (lastExistingId && lastFetchedId && lastExistingId > lastFetchedId) {
          console.log('⚠️ SKIPPING replace - store has newer messages:', {
            storeLastId: lastExistingId,
            serverLastId: lastFetchedId
          });
          state.chatMessages[chatId].isLoading = false;
          
          // Обновляем lastMessage в списке чатов из существующих сообщений
          if (existingMessages.length > 0) {
            const lastMsg = existingMessages[existingMessages.length - 1];
            const chat = state.chats.find(c => c.id === chatId);
            if (chat) {
              chat.lastMessage = lastMsg;
              chat.lastMessageAt = lastMsg.createdAt || lastMsg.created_at;
            }
          }
          
          persistState(state, state.currentUserId);
          return;
        }
        
        console.log('⚠️ REPLACING messages:', {
          oldCount: existingMessages.length,
          newCount: messages.length,
          lastExistingId,
          lastFetchedId,
          hasChanges
        });
        
        // Обновляем сообщения (бэкенд возвращает последние N, поэтому заменяем весь массив)
        state.chatMessages[chatId] = { messages, hasMore, isLoading: false };
        
        // ВАЖНО: Обновляем lastMessage в списке чатов из загруженных сообщений
        // Это критично для отображения последнего сообщения после перезагрузки страницы
        if (messages.length > 0) {
          const lastMsg = messages[messages.length - 1];
          let chat = state.chats.find(c => c.id === chatId);
          if (!chat) {
            // Если чата нет в списке, создаем его (может быть после перезагрузки)
            chat = {
              id: chatId,
              type: 'private',
              members: [],
              participants: [],
              lastMessage: lastMsg,
              lastMessageAt: lastMsg.createdAt || lastMsg.created_at,
            };
            state.chats.push(chat);
          } else {
            // Обновляем lastMessage только если его нет или если новое сообщение новее
            const currentLastMsgTime = chat.lastMessageAt || (chat.lastMessage?.createdAt || chat.lastMessage?.created_at);
            const newLastMsgTime = lastMsg.createdAt || lastMsg.created_at;
            if (!chat.lastMessage || !currentLastMsgTime || new Date(newLastMsgTime) > new Date(currentLastMsgTime)) {
              chat.lastMessage = lastMsg;
              chat.lastMessageAt = newLastMsgTime;
            }
          }
        }
        
        persistState(state, state.currentUserId);
        
        if (state.activeChatId === chatId) {
          state.unreadCounts[chatId] = 0;
        } else {
          // Пересчитываем непрочитанные сообщения (более надежно, чем инкремент/декремент)
          const unreadCount = messages.filter(m => 
            m.user?.id !== currentUserId && !m.isRead
          ).length;
          state.unreadCounts[chatId] = unreadCount;
        }
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        const { chatId } = action.meta.arg;
        
        if (state.chatMessages[chatId]) {
          state.chatMessages[chatId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Получение списка чатов
      .addCase(fetchUserChats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserChats.fulfilled, (state, action) => {
        state.isLoading = false;
        const { chats, hasMore } = action.payload;
        
        // Обновляем список чатов, сохраняя lastMessage из существующих чатов если новый чат не имеет lastMessage
        const existingChatsMap = new Map(state.chats.map(c => [c.id, c]));
        
        const updatedChats = chats.map(newChat => {
          const existingChat = existingChatsMap.get(newChat.id);
          // Если новый чат не имеет lastMessage, но есть в существующих - берем оттуда
          if (!newChat.lastMessage && existingChat?.lastMessage) {
            return {
              ...newChat,
              lastMessage: existingChat.lastMessage,
              lastMessageAt: existingChat.lastMessageAt || existingChat.lastMessage?.createdAt
            };
          }
          // Если есть сообщения в chatMessages, используем последнее как fallback
          if (!newChat.lastMessage && state.chatMessages[newChat.id]?.messages?.length > 0) {
            const messages = state.chatMessages[newChat.id].messages;
            const lastMsg = messages[messages.length - 1];
            return {
              ...newChat,
              lastMessage: lastMsg,
              lastMessageAt: lastMsg?.createdAt || lastMsg?.created_at || newChat.lastMessageAt
            };
          }
          return newChat;
        });
        
        // Если это первая загрузка, заменяем список
        if (state.chats.length === 0) {
          state.chats = updatedChats;
        } else {
          // Иначе добавляем новые чаты и обновляем существующие
          const existingIds = new Set(state.chats.map(c => c.id));
          const newChats = updatedChats.filter(c => !existingIds.has(c.id));
          // Обновляем существующие чаты данными с сервера (с сохранением lastMessage)
          const mergedChats = state.chats.map(existingChat => {
            const serverChat = updatedChats.find(c => c.id === existingChat.id);
            // Если серверный чат есть, используем его (уже с сохраненным lastMessage)
            if (serverChat) {
              return serverChat;
            }
            // Иначе оставляем существующий
            return existingChat;
          });
          state.chats = [...newChats, ...mergedChats];
        }
        
        state.hasMoreChats = hasMore;
        
        // Сбрасываем флаг needsChatRefresh после успешного обновления
        state.needsChatRefresh = false;
        
        // Инициализируем счетчики непрочитанных
        updatedChats.forEach(chat => {
          if (chat.unreadCount > 0) {
            state.unreadCounts[chat.id] = chat.unreadCount;
          }
        });
        persistState(state, state.currentUserId);
      })
      .addCase(fetchUserChats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Создание чата
      .addCase(createChat.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createChat.fulfilled, (state, action) => {
        state.isLoading = false;
        const chat = action.payload;
        
        // Удаляем возможный дубль по id, затем добавляем в начало
        state.chats = state.chats.filter(c => c.id !== chat.id);
        state.chats.unshift(chat);
        
        // Инициализируем сообщения для нового чата
        state.chatMessages[chat.id] = { messages: [], hasMore: true, isLoading: false };
        state.unreadCounts[chat.id] = 0;
        persistState(state, state.currentUserId);
      })
      .addCase(createChat.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Отметка сообщений как прочитанных
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const { chatId, messageIds } = action.payload;
        
        console.log('✅ markMessagesAsRead.fulfilled:', { chatId, messageIds, messageCount: messageIds.length });
        
        if (state.chatMessages[chatId]) {
          let updatedCount = 0;
          const messageIdsSet = new Set(messageIds.map(id => Number(id)));
          
          // Создаем НОВЫЙ массив для триггера ре-рендера
          state.chatMessages[chatId].messages = state.chatMessages[chatId].messages.map(message => {
            const messageIdNum = Number(message.id);
            if (messageIdsSet.has(messageIdNum)) {
              updatedCount++;
              return {
                ...message,
                isRead: true,
                isDelivered: true
              };
            }
            return message;
          });
          console.log('✅ Updated', updatedCount, 'messages as read in Redux');
        }
        
        state.unreadCounts[chatId] = 0;
      })
      .addCase(markMessagesAsRead.rejected, (state, action) => {
        console.error('Failed to mark messages as read:', action.payload);
      })
      
      // Обработка логина/логаута из authSlice
      .addCase(login.fulfilled, (state, action) => {
        // Извлекаем user ID из ответа логина
        // В authSlice структура: action.payload = { user: {...}, token: ... }
        const userData = action.payload?.user || action.payload;
        const userId = userData?.id || null;
        if (userId) {
          // Если пользователь изменился, очищаем все чаты
          if (state.currentUserId && state.currentUserId !== userId) {
            state.chats = [];
            state.chatMessages = {};
            state.unreadCounts = {};
            state.needsChatRefresh = false;
            try {
              removeStored(STORAGE_KEY);
            } catch (_) {
              // ignore
            }
          }
          state.currentUserId = userId;
          
          // Загружаем сохраненные данные для нового пользователя
          const persisted = loadPersistedState(userId);
          if (persisted) {
            state.chats = persisted.chats || [];
            state.chatMessages = persisted.chatMessages || {};
            state.unreadCounts = persisted.unreadCounts || {};
            state.hasMoreChats = persisted.hasMoreChats ?? true;
          }
        }
      })
      .addCase(logout, (state) => {
        // Очищаем все чаты при выходе
        state.chats = [];
        state.chatMessages = {};
        state.unreadCounts = {};
        state.currentUserId = null;
        state.needsChatRefresh = false;
        try {
          removeStored(STORAGE_KEY);
        } catch (_) {
          // ignore
        }
      });
  },
});

// Экспортируем actions
export const {
  addMessage,
  updateMessage,
  updateMessagesStatus,
  removeMessage,
  addMessageOptimistic,
  updateReadStatus,
  setActiveChat,
  clearError,
  clearNeedsChatRefresh,
  resetMessagesState,
  clearAllChats,
  setCurrentUser,
} = messagesSlice.actions;

// Селекторы
export const selectChats = (state) => state.messages.chats;
export const selectChatMessages = (state, chatId) => {
  return state.messages.chatMessages[chatId] || { messages: [], hasMore: true, isLoading: false };
};
export const selectUnreadCount = (state, chatId) => state.messages.unreadCounts[chatId] || 0;
export const selectTotalUnreadCount = (state) => 
  Object.values(state.messages.unreadCounts).reduce((sum, count) => sum + count, 0);
export const selectMessagesLoading = (state) => state.messages.isLoading;
export const selectMessagesError = (state) => state.messages.error;
export const selectActiveChatId = (state) => state.messages.activeChatId;
export const selectHasMoreChats = (state) => state.messages.hasMoreChats;
export const selectNeedsChatRefresh = (state) => state.messages.needsChatRefresh;

export default messagesSlice.reducer;
